import React, { useState, useEffect } from 'react';
import { unifiedApi } from '../../api/unifiedApi';

interface LockPasswordInitModalProps {
  noteId: string;
  noteTitle: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onLockNote?: (noteId: string) => void;
}

export const LockPasswordInitModal: React.FC<LockPasswordInitModalProps> = ({
  noteId,
  noteTitle,
  isOpen,
  onClose,
  onSuccess,
  onLockNote,
}) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // 重置状态当弹窗打开时
  useEffect(() => {
    if (isOpen) {
      setPassword('');
      setConfirmPassword('');
      setError('');
    }
  }, [isOpen]);

  // 验证密码格式（4位字母数字组合）
  const validatePassword = (pwd: string): boolean => {
    return /^[a-zA-Z0-9]{4}$/.test(pwd);
  };

  // 处理密码输入
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 4);
    setPassword(value);
    setError('');
  };

  // 处理确认密码输入
  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 4);
    setConfirmPassword(value);
    setError('');
  };

  // 检查密码是否匹配
  const passwordsMatch = password === confirmPassword && password.length === 4;

  // 处理确认
  const handleConfirm = async () => {
    if (!validatePassword(password)) {
      setError('请输入4位字母数字密码');
      return;
    }

    if (!passwordsMatch) {
      setError('两次输入的密码不一致');
      return;
    }

    setIsLoading(true);
    try {
      console.log('[LockPasswordInitModal] 设置密码:', password);
      // 先设置密码
      const passwordResult = await unifiedApi.setLockPassword(password);
      console.log('[LockPasswordInitModal] 密码设置结果:', passwordResult);
      
      // 然后上锁笔记（通过 Redux action 以便立即更新 UI）
      console.log('[LockPasswordInitModal] 上锁笔记:', noteId);
      if (onLockNote) {
        onLockNote(noteId);
      } else {
        await unifiedApi.lockNote(noteId);
      }
      
      onSuccess();
      onClose();
    } catch (err) {
      console.error('[LockPasswordInitModal] 设置失败:', err);
      setError('设置失败，请重试');
    } finally {
      setIsLoading(false);
    }
  };

  // 处理取消
  const handleCancel = () => {
    setPassword('');
    setConfirmPassword('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6">
        {/* 标题 */}
        <div className="flex items-center mb-4">
          <svg className="w-6 h-6 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" strokeWidth="2" />
            <circle cx="12" cy="16" r="1" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" strokeWidth="2" />
          </svg>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            初始化上锁密码
          </h2>
        </div>

        {/* 说明文字 */}
        <div className="mb-6 text-sm text-gray-600 dark:text-gray-300 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
          <p className="mb-1">
            这是您首次为笔记设置上锁密码。
          </p>
          <p>
            笔记「<span className="font-medium text-gray-900 dark:text-white">{noteTitle}</span>」将被上锁保护。
          </p>
        </div>

        {/* 密码输入 */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            设置4位密码
          </label>
          <input
            type="password"
            value={password}
            onChange={handlePasswordChange}
            placeholder="请输入4位字母或数字"
            maxLength={4}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-center text-lg tracking-widest"
          />
        </div>

        {/* 确认密码输入 */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            确认密码
          </label>
          <input
            type="password"
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            placeholder="请再次输入4位密码"
            maxLength={4}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-center text-lg tracking-widest"
          />
          {confirmPassword.length >= 4 && password.length >= 4 && (
            <p className={`mt-1 text-xs ${
              passwordsMatch ? 'text-green-500' : 'text-red-500'
            }`}>
              {passwordsMatch ? '密码一致' : '密码不一致'}
            </p>
          )}
        </div>

        {/* 错误提示 */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* 按钮 */}
        <div className="flex gap-3">
          <button
            onClick={handleCancel}
            disabled={isLoading}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            取消
          </button>
          <button
            onClick={handleConfirm}
            disabled={!passwordsMatch || isLoading}
            className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? '设置中...' : '确认上锁'}
          </button>
        </div>

        {/* 提示 */}
        <p className="mt-4 text-xs text-gray-500 dark:text-gray-400 text-center">
          密码仅用于解锁笔记查看，不会加密笔记内容
        </p>
      </div>
    </div>
  );
};

export default LockPasswordInitModal;
